import { useState } from "react";

// Gráfica de evolución para cualquier métrica: recibe puntos {date, value} y
// la unidad con la que etiquetarlos.
export function MetricChart({ data, unit = "", width = 320, height = 160, gradientId = "metric" }) {
  const [tooltip, setTooltip] = useState(null);

  if (!data || data.length === 0) return null;

  const values = data.map((d) => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pad = 16;

  const points = data.map((d, i) => ({
    x: pad + (i / Math.max(data.length - 1, 1)) * (width - pad * 2),
    y: height - pad - ((d.value - min) / range) * (height - pad * 2),
  }));

  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x} ${height - pad} L ${points[0].x} ${height - pad} Z`;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (width / rect.width);
    let nearestIdx = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = i;
      }
    });
    setTooltip(minDist < 30 ? { ...points[nearestIdx], ...data[nearestIdx] } : null);
  };

  // Mantiene la caja del tooltip dentro del lienzo en los extremos.
  const ttX = tooltip ? Math.min(Math.max(tooltip.x, pad + 44), width - pad - 44) : 0;
  const ttY = tooltip ? Math.max(tooltip.y - 50, pad) : 0;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible cursor-crosshair"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTooltip(null)}
    >
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad}
        stroke="hsl(var(--border))" strokeWidth={1} />
      <line x1={pad} y1={pad} x2={width - pad} y2={pad}
        stroke="hsl(var(--border))" strokeWidth={1} strokeDasharray="3,4" opacity={0.5} />

      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.2" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>

      {points.length > 1 && <path d={areaD} fill={`url(#${gradientId})`} />}
      {points.length > 1 && (
        <path d={pathD} stroke="hsl(var(--primary))" strokeWidth={2.5}
          fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}

      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y}
          r={i === points.length - 1 ? 5 : 3}
          fill={i === points.length - 1 ? "hsl(var(--success))" : "hsl(var(--primary))"}
          stroke="hsl(var(--background))" strokeWidth={2} />
      ))}

      <text x={pad} y={height - 1} fill="hsl(var(--muted-foreground))" fontSize={10}
        fontFamily="Outfit" fontWeight={500}>{min} {unit}</text>
      <text x={width - pad} y={height - 1} fill="hsl(var(--muted-foreground))" fontSize={10}
        fontFamily="Outfit" fontWeight={500} textAnchor="end">{max} {unit}</text>

      {tooltip && (
        <g>
          <line x1={tooltip.x} y1={pad} x2={tooltip.x} y2={height - pad}
            stroke="hsl(var(--primary))" strokeWidth={1} strokeDasharray="3,3" opacity={0.5} />
          <rect x={ttX - 44} y={ttY} width={88} height={38} rx={6}
            fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth={1} opacity={0.95} />
          <text x={ttX} y={ttY + 14} textAnchor="middle"
            fill="hsl(var(--foreground))" fontSize={12} fontWeight="700" fontFamily="Outfit">
            {tooltip.value} {unit}
          </text>
          <text x={ttX} y={ttY + 29} textAnchor="middle"
            fill="hsl(var(--muted-foreground))" fontSize={9} fontFamily="Outfit">
            {new Date(tooltip.date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "2-digit" })}
          </text>
        </g>
      )}
    </svg>
  );
}
